import TopBar from "../components/TopBar";
import cities from "../data/cities";

export default function ArrivalScreen({ onStart, puzzle, openHowToPlay, onStats }) {
  // Styles for fluffy clouds and floating animations
  const cloudStyles = `
    @keyframes floatCloudLeft {
      0% { transform: translateX(0px); }
      50% { transform: translateX(15px); }
      100% { transform: translateX(0px); }
    }
    @keyframes floatCloudRight {
      0% { transform: translateX(0px); }
      50% { transform: translateX(-15px); }
      100% { transform: translateX(0px); }
    }

    /* FLUFFY CLOUD BASE */
    .cloud-shape {
      position: absolute;
      background: rgba(255, 255, 255, 0.92);
      border-radius: 50px;
      pointer-events: none;
      filter: drop-shadow(0 6px 12px rgba(255, 255, 255, 0.6));
      z-index: 0;
    }

    /* TOP PUFFS */
    .cloud-shape::before,
    .cloud-shape::after {
      content: '';
      position: absolute;
      background: rgba(255, 255, 255, 0.92);
      border-radius: 50%;
    }

    .cloud-shape::before {
      width: 50%;
      height: 110%;
      top: -45%;
      left: 15%;
    }

    .cloud-shape::after {
      width: 42%;
      height: 90%;
      top: -35%;
      right: 15%;
    }

    /* POSITIONS & SIZES */
    .cloud1 {
      left: -20px;
      top: 80px;
      width: 170px;
      height: 50px;
      animation: floatCloudLeft 8s ease-in-out infinite;
    }

    .cloud2 {
      right: -25px;
      top: 160px;
      width: 190px;
      height: 55px;
      animation: floatCloudRight 9s ease-in-out infinite;
    }

    .cloud3 {
      left: -30px;
      top: 450px;
      width: 160px;
      height: 48px;
      animation: floatCloudLeft 10s ease-in-out infinite;
    }

    .cloud4 {
      right: -30px;
      top: 620px;
      width: 180px;
      height: 52px;
      animation: floatCloudRight 11s ease-in-out infinite;
    }
  `;

  // CLEAN LOADING SCREEN WITH CLOUDS & DAYTIME THEME
  if (!puzzle) {
    return (
      <div style={styles.page}>
        <style>{cloudStyles}</style>
        <div className="cloud-shape cloud1"></div>
        <div className="cloud-shape cloud2"></div>
        <div className="cloud-shape cloud3"></div>
        <div className="cloud-shape cloud4"></div>
        
        <div style={styles.loadingContainer}>
          <div style={styles.loadingText}>Loading...</div>
        </div>
      </div>
    );
  }

  const departureCity = cities.find((c) => c.name === puzzle.departure);

  // Flightle number: 3 August 2026 = FL001
  const firstFlightle = new Date("2026-08-03T00:00:00");
  const today = new Date();
  const daysSinceFirst = Math.floor(
    (new Date(today.getFullYear(), today.getMonth(), today.getDate()) - firstFlightle) /
      (1000 * 60 * 60 * 24)
  );
  const flightNumber = `FL${String(daysSinceFirst + 1).padStart(3, "0")}`;

  // Local date, e.g. "23 August 2026"
  const flightDate = today.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div style={styles.page}>
      <style>{cloudStyles}</style>
      
      {/* Google Font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@500;700&display=swap"
        rel="stylesheet"
      />

      {/* CLOUDS VISIBLE ON MOBILE & DESKTOP */}
      <div className="cloud-shape cloud1"></div>
      <div className="cloud-shape cloud2"></div>
      <div className="cloud-shape cloud3"></div>
      <div className="cloud-shape cloud4"></div>

      <TopBar showBack={false} onHelp={openHowToPlay} onStats={onStats} />

      {/* MAIN CARD */}
      <div style={styles.card}>
        <div style={styles.flightMeta}>
          <div>{flightDate}</div>
          <div>{flightNumber}</div>
        </div>

        <h1 style={{ margin: "28px 0 10px" }}>
          You’ve arrived in a new city
        </h1>

        <div style={styles.routeWrapper}>
          <div style={styles.routeSvg}>
            <svg width="300" height="56" viewBox="0 0 300 56">
              <path
                d="M44 32 C 80 5, 120 65, 170 35 S 240 5, 282 32"
                stroke="#93c5fd"
                strokeWidth="3"
                fill="none"
                strokeDasharray="7 7"
                strokeLinecap="round"
              />
            </svg>

            <div style={styles.routePlane}>✈️</div>

            {/* Departure Pin */}
            <div
              style={{
                ...styles.pin,
                left: 39, // Matches the M44 in the SVG path
                position: "absolute",
                top: 12, // Relative to the routeSvg container now
                transform: "translateX(-50%)",
              }}
            >
              <div style={styles.routePin}>📍</div>
              <div style={styles.routeCity}>
                {departureCity?.name} {departureCity?.flag}
              </div>
            </div>

            {/* Arrival Pin */}
            <div
              style={{
                ...styles.pin,
                left: 287, // Matches the 282 in the SVG path
                position: "absolute",
                top: 12, // Relative to the routeSvg container now
                transform: "translateX(-50%)",
              }}
            >
              <div style={styles.routePin}>📍</div>
              <div style={styles.routeCity}>???</div>
            </div>
          </div>
        </div>

        <p style={styles.flightTime}>
          Flight duration: {puzzle.flightTime} hour{puzzle.flightTime > 1 ? "s" : ""}
        </p>
      </div>

      {/* OUTSIDE BUTTON */}
      <button style={styles.button} onClick={onStart}>
        Start your journey
      </button>

      <div style={styles.howTo}>
        <h3>Can you guess the destination city?</h3>

        <div style={styles.steps}>
          <div>
            <div style={styles.stepIcon}>🧭</div>
            Each guess is a city
          </div>

          <div>
            <div style={styles.stepIcon}>🔎</div>
            Get clues for each wrong guess
          </div>

          <div>
            <div style={styles.stepIcon}>🏆</div>
            Guess in as few tries as possible
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    textAlign: "center",
    paddingTop: 60,
    fontFamily: "'Inter', sans-serif",
    background: `linear-gradient(to bottom, #deecfc 0%, #dff3ff 45%, #eef7ff 100%)`,
    overflow: "hidden",
    position: "relative",
    minHeight: "100vh",
  },

  /* LOADING SCREEN STYLING */
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "calc(100vh - 60px)",
  },
  loadingText: {
    fontSize: 22,
    fontWeight: 700,
    color: "#334155",
    letterSpacing: 0.5,
  },

  card: {
    position: "relative",
    zIndex: 1,
    background: "rgba(255,255,255,0.94)",
    borderRadius: 28,
    boxShadow: "0 12px 35px rgba(15,23,42,0.12)",
    padding: "22px 20px",
    margin: "18px auto 0",
    width: "86%",
    maxWidth: 500,
    backdropFilter: "blur(10px)",
  },

  button: {
    position: "relative",
    zIndex: 1,
    marginTop: 26,
    padding: "15px 28px",
    fontSize: 20,
    fontWeight: 700,
    cursor: "pointer",
    borderRadius: 18,
    border: "none",
    background: "#6ca965",
    color: "white",
    boxShadow: "0 10px 24px rgba(37,99,235,0.35)",
    width: "90%",
    maxWidth: 420,
  },

  topBar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: 50,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 12px",
    background: "#deecfc",
    backdropFilter: "blur(8px)",
    zIndex: 1000,
  },

  routeWrapper: {
    margin: "12px 0 14px",
  },

  routeSvg: {
    position: "relative",
    width: 300,
    margin: "0 auto",
    height: 56,
  },

  routePlane: {
    position: "absolute",
    top: 26,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: 26,
    color: "#2563eb",
  },

  routePins: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
  },

  pinLeft: {
    position: "absolute",
    left: 0,
    top: 0,
    textAlign: "center",
  },

  pinRight: {
    position: "absolute",
    right: 0,
    top: 0,
    textAlign: "center",
  },

  routePin: {
    fontSize: 22,
  },

  routeCity: {
    marginTop: -5,
    fontWeight: 700,
    fontSize: 18,
  },

  howTo: {
    position: "relative",
    zIndex: 1,
    marginTop: 60,
    background: "rgba(255,255,255,0.75)",
    borderRadius: 28,
    padding: 10,
    width: "90%",
    maxWidth: 520,
    marginInline: "auto",
  },

  steps: {
    display: "grid",
    gridTemplateColumns: "repeat(3,1fr)",
    gap: 16,
    marginTop: 20,
  },

  stepIcon: {
    fontSize: 28,
    marginBottom: 12,
  },

  flightTime: {
    marginTop: 30,
    marginBottom: 0,
    fontSize: 18,
    fontWeight: 800,
    color: "#0f172a",
  },

  pin: {
    textAlign: "center",
  },

  flightMeta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontFamily: "'Roboto Mono', monospace",
    fontSize: 12.5,
    fontWeight: 700,
    color: "#64748b",
    letterSpacing: 0.3,
    padding: "0 2px",
  },
};